import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import Header from "@/components/public/Header";
import Footer from "@/components/public/Footer";

function renderBody(body) {
  const lines = (body || "").split("\n");
  const blocks = [];
  let list = [];
  const flush = () => {
    if (list.length) { blocks.push({ type: "ul", items: list }); list = []; }
  };
  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) { flush(); return; }
    if (line.startsWith("- ")) { list.push(line.slice(2)); }
    else { flush(); blocks.push({ type: "p", text: line }); }
  });
  flush();
  return blocks;
}

export default function PrivacyPolicy() {
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState({});
  useSeo("/privacy-policy");

  useEffect(() => {
    api.get("/public/privacy-policy").then((r) => setPage(r.data)).catch(() => setPage({ error: true }));
    api.get("/public/homepage").then((r) => setSettings(r.data.settings || {})).catch(() => {});
  }, []);

  if (!page) return <div className="min-h-screen" />;

  const hero = page.hero || {};
  const sections = page.sections || [];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header phone={settings.phone} />

      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
        <div className="container-cz pt-8 pb-12 sm:pt-10 sm:pb-16">
          <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-8" data-testid="privacy-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
            <span className="text-slate-700 font-medium">Privacy Policy</span>
          </nav>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary bg-sky-50 rounded-full px-3 py-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> {hero.eyebrow || "Legal"}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#16161a] mt-5">
              {hero.title || "Privacy Policy"}
            </h1>
            {hero.subtitle && <p className="text-base sm:text-lg text-slate-600 mt-4 leading-relaxed">{hero.subtitle}</p>}
            {hero.last_updated && (
              <p className="text-sm text-slate-500 mt-5" data-testid="privacy-last-updated">
                <span className="font-medium text-slate-700">Last Updated:</span> {hero.last_updated}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="flex-1">
        <div className="container-cz py-12 sm:py-16">
          <article className="mx-auto max-w-3xl" data-testid="privacy-content">
            {sections.map((s) => {
              const Heading = s.level === "h3" ? "h3" : "h2";
              const headingCls = s.level === "h3"
                ? "font-display text-lg sm:text-xl font-bold text-slate-900 mt-8 mb-3"
                : "font-display text-2xl sm:text-3xl font-bold text-[#16161a] mt-12 mb-4 pb-2 border-b border-slate-100 first:mt-0";
              return (
                <section key={s.id} className="scroll-mt-24" data-testid={`privacy-section-${s.id}`}>
                  <Heading className={headingCls}>{s.heading}</Heading>
                  {renderBody(s.body).map((b, i) =>
                    b.type === "ul" ? (
                      <ul key={i} className="list-disc pl-5 space-y-2 text-slate-600 leading-relaxed my-3 marker:text-primary">
                        {b.items.map((it, j) => <li key={j}>{it}</li>)}
                      </ul>
                    ) : (
                      <p key={i} className="text-slate-600 leading-relaxed my-3">{b.text}</p>
                    )
                  )}
                </section>
              );
            })}
          </article>
        </div>
      </main>

      <Footer settings={settings} />
    </div>
  );
}
